import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, Search, Tag, X, AlertTriangle } from "lucide-react";

// Multi-select from the Price List.
// Technicians: DESCRIPTIONS ONLY (no prices are ever fetched).
// Admins (showRate=true): also fetches priced list + shows an editable per-line Rate override.
// value = [{ key, description, unit, qty, rate }]
export default function ChargeCodesPicker({ value, onChange, showRate = false, jobId }) {
  const selected = value || [];
  const [items, setItems] = useState([]);
  const [priceByKey, setPriceByKey] = useState({});
  const [minByKey, setMinByKey] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = jobId ? { params: { job_id: jobId } } : undefined;
    if (showRate) {
      api.get("/price-list-items-priced", params).then((r) => {
        setItems(r.data);
        const m = {}, mm = {}; r.data.forEach((it) => { m[it.key] = it.price; if (it.min_lm) mm[it.key] = it.min_lm; });
        setPriceByKey(m); setMinByKey(mm);
      }).catch(() => {});
    } else {
      api.get("/price-list-items", params).then((r) => setItems(r.data)).catch(() => {});
    }
  }, [showRate, jobId]);

  const isSel = (key) => selected.some((s) => s.key === key);
  const toggle = (it) => {
    if (isSel(it.key)) onChange(selected.filter((s) => s.key !== it.key));
    else onChange([...selected, { key: it.key, description: it.label, unit: it.unit, qty: "", rate: "" }]);
  };
  const setField = (key, field, val) => onChange(selected.map((s) => (s.key === key ? { ...s, [field]: val } : s)));
  const matches = q ? items.filter((it) => it.label.toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div data-testid="charge-codes-picker">
      {selected.length > 0 && (
        <div className="space-y-1.5 mb-2" data-testid="charge-codes-selected">
          {selected.map((s) => {
            const min = minByKey[s.key];
            const under = min && s.qty !== "" && Number(s.qty) > 0 && Number(s.qty) < min;
            return (
            <div key={s.key}>
            <div className={`flex items-center gap-2 bg-navy/5 border rounded-lg px-2.5 py-1.5 ${under ? "border-amber-300" : "border-navy/10"}`}>
              <Tag className="w-3.5 h-3.5 text-navy shrink-0" />
              <span className="flex-1 min-w-0 text-sm text-navy break-words leading-snug">{s.description}</span>
              <input data-testid={`charge-qty-${s.key}`} type="number" min="0" value={s.qty}
                onChange={(e) => setField(s.key, "qty", e.target.value)} placeholder="Qty"
                className="w-14 text-sm border border-slate-200 rounded-md px-2 py-1 text-right outline-none focus:border-navy" />
              <span className="text-xs text-slate-400 w-7">{s.unit === "lm" ? "lm" : s.unit === "day" ? "day" : "ea"}</span>
              {showRate && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-400">$</span>
                  <input data-testid={`charge-rate-${s.key}`} type="number" min="0" step="0.01" value={s.rate ?? ""}
                    onChange={(e) => setField(s.key, "rate", e.target.value)}
                    placeholder={priceByKey[s.key] != null ? Number(priceByKey[s.key]).toFixed(2) : "rate"}
                    title="Rate override — leave blank to use the Price List rate"
                    className="w-20 text-sm border border-slate-200 rounded-md px-2 py-1 text-right outline-none focus:border-navy" />
                </div>
              )}
              <button type="button" onClick={() => toggle({ key: s.key })} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
            </div>
            {under && (
              <p data-testid={`charge-under-min-${s.key}`} className="flex items-center gap-1 text-[11px] text-amber-700 mt-0.5 ml-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />Below the {min} lm minimum — this line will be billed at the {min} lm minimum charge.
              </p>
            )}
            </div>
            );
          })}
          {showRate && <p className="text-[11px] text-slate-400">Rate is an override (Admin only). Leave blank to use the Price List rate (shown as placeholder).</p>}
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="charge-codes-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search Price List items…" className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-navy" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2 max-h-56 overflow-y-auto">
        {matches.map((it) => (
          <button key={it.key} type="button" data-testid={`charge-code-${it.key}`} onClick={() => toggle(it)}
            className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-sm text-left transition-colors ${isSel(it.key) ? "border-navy bg-navy/5" : "border-slate-200 hover:bg-slate-50"}`}>
            <span className="flex-1 min-w-0 break-words leading-snug text-navy">{it.label}</span>
            {showRate && it.price != null && <span className="text-[10px] text-slate-400 shrink-0">${Number(it.price).toFixed(2)}</span>}
            <span className="text-[10px] uppercase tracking-wide text-slate-400 shrink-0">{it.unit}</span>
            {isSel(it.key) && <Check className="w-4 h-4 text-navy shrink-0" />}
          </button>
        ))}
        {matches.length === 0 && <p className="col-span-full text-sm text-slate-400 px-1 py-2">No matching item.</p>}
      </div>
    </div>
  );
}
